import { memo, useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { useQueryClient } from "@tanstack/react-query";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PaginationType,
  TableHeaderFilter,
  UseQueryProps,
} from "@lib/types/Api";
import { toast } from "react-toastify";
import { WalletAddressModal } from "@lib/components/PageComponents/Wallet";
import { type TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { useIntl } from "react-intl";
import WalletItemDetail from "./WalletItemDetail";
import { TbListDetails } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { useSendByteWalletAddressQueries } from "@lib/apiQueries";
import { AppContext } from "@lib/types";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { SendByteWallet, SendByteWalletAddress } from "@lib/types/SendByte";
import { getWalletLinkNoFilter } from "../Sidebar/utils";

type Props = {
  sendByteWalletsQuery: UseQueryProps<PaginationType<SendByteWallet>>;
  context: AppContext;
  detailContext?: AppContext;
  customerId?: string;
  leadId?: string;
  pageIsActive?: boolean;
};

const WalletAddressList = ({
  sendByteWalletsQuery,
  context,
  detailContext,
  customerId,
  leadId,
  pageIsActive = true,
}: Props) => {
  const { walletId } = useParams();
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();

  const filter = []
    .concat(walletId ? [`walletId||$eq||${walletId}`] : [])
    .concat(...(state?.filter || []))
    .concat(
      ...(customerId ? [`customerId||$eq||${customerId}`] : []),
      ...(leadId ? [`customerUserId||$eq||${leadId}`] : []),
    );

  const {
    params,
    setParams,
    handleRemoveFilters: handleRemoveFiltersParams,
    onPageChange,
  } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer", "customerUser", "wallet"],
    filter: filter,
  });

  const handleRemoveFilter = (filter: string[]) => {
    handleRemoveFiltersParams(filter);

    if (filter.includes("walletId")) {
      navigate(getWalletLinkNoFilter(context));
      return;
    }
  };

  useEffect(() => {
    setParams({
      filter: walletId ? [`walletId||$eq||${walletId}`] : [],
    });
  }, [walletId]);

  const { sendByteWalletAddressQuery, deleteSendByteWalletAddressMutation } =
    useSendByteWalletAddressQueries({
      params,
      messages,
      queryKey: ["sendbyte-wallet-address", params],
      enabled: !!params && pageIsActive,
    });
  const { data, isLoading, isFetching } = sendByteWalletAddressQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (deleteSendByteWalletAddressMutation.isSuccess) {
      setScanning(true);
    }
  }, [deleteSendByteWalletAddressMutation.isSuccess]);

  const queryClient = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] =
    useState<SendByteWalletAddress>();

  useEffect(() => {
    if (scanning) {
      toast.promise(
        new Promise((resolve) => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: ["billing-wallet-address"],
            });
            queryClient.removeQueries({
              queryKey: ["customer-wallet-address"],
            });
            queryClient.removeQueries({
              queryKey: ["scanned-transaction"],
            });
            setScanning(false);
            return resolve(true);
          }, 5000);
        }),
        {
          pending: messages["scanRequest.pending"] as string,
          success: messages["scanRequest.success"] as string,
          error: messages["scanRequest.error"] as string,
        },
      );
    }
  }, [scanning]);

  useEffect(() => {
    if (!openModal) setSelectedAddress(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectAddress = (walletAddress: SendByteWalletAddress) => {
    setSelectedAddress(walletAddress);
    toggle(true);
  };

  const onChange: TableProps<SendByteWalletAddress>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        apiData,
        deleteQuery: deleteSendByteWalletAddressMutation,
        openModal,
        handleSelectAddress,
        params,
        messages,
        navigate,
      }),
    [
      apiData,
      deleteSendByteWalletAddressMutation,
      openModal,
      handleSelectAddress,
      params,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["walletId", "customerId", "customerUserId"];
    }
    if (context === AppContext.CUSTOMER_LEAD) {
      return ["walletId", "customerUserId"];
    }
    if (context === AppContext.CUSTOMER) {
      return ["walletId", "customerId"];
    }
    return [];
  }, [context, detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params, columns]);

  return (
    <>
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={sendByteWalletAddressQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilter}
          plusProps={
            context === AppContext.CUSTOMER_LEAD && detailContext
              ? undefined
              : {
                  onClick: () => toggle(true),
                  disabled: isLoading,
                  loading: isLoading,
                }
          }
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching || scanning}
          minusHeight={20}
          expandable={{
            // showExpandColumn: false,
            columnTitle: "#",
            columnWidth: "50px",
            fixed: "right",
            expandIcon: ({ expanded, record, onExpand }) => (
              <ActionButton
                type={expanded ? "close" : "detail"}
                icon={expanded ? <IoMdClose /> : <TbListDetails />}
                color={expanded ? "red" : "green"}
                onClick={(e) => onExpand(record, e)}
                noLabel
              />
            ),
            expandedRowRender: (record) => <WalletItemDetail item={record} />,
            onExpand(expanded, record) {
              try {
                const find = document.querySelector(
                  `[data-row-key="${record.id}"]`,
                );
                if (find) {
                  find.setAttribute(
                    "data-row-expanded",
                    expanded ? "true" : "false",
                  );
                }
              } catch (error) {}
            },
          }}
        />

        <WalletAddressModal.SendByteWalletAddressModal
          sendByteWalletsQuery={sendByteWalletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          sendByteWalletAddress={selectedAddress}
          customerId={customerId}
        />
      </AppsContent>
    </>
  );
};

export default memo(WalletAddressList);
